import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { MessagesSquare } from 'lucide-react';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
// Chat category configuration
export const CHAT_CATEGORY_CONFIG = [
  { id: 'General', name: 'General', icon: MessagesSquare },
] as const;

export type ChatCategoryId = typeof CHAT_CATEGORY_CONFIG[number]['id'];

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

interface UseYourChatsOptions {
  autoFetchOnMount?: boolean;
  pageSize?: number;
}

// Module-level state to persist across re-renders and navigation
let persistedYourChatsExpanded = false;
let persistedExpandedCategories: string[] = [];

// Module-level listeners for chat updates (allows external components to trigger updates)
type ChatUpdateListener = (sessionId: string, newTitle: string) => void;
const chatUpdateListeners: Set<ChatUpdateListener> = new Set();

// Export function to trigger chat title update from anywhere
export const triggerChatTitleUpdate = (sessionId: string, newTitle: string) => {
  chatUpdateListeners.forEach(listener => listener(sessionId, newTitle));
};

// NEW: Listener for new chat additions
type NewChatListener = (chat: NewChatData) => void;
const newChatListeners: Set<NewChatListener> = new Set();

export interface NewChatData {
  sessionId: string;
  sessionTitle: string;
  category: 'General';
  startedAt: string;
}

// Export function to trigger new chat addition from anywhere
export const triggerNewChatAdded = (chat: NewChatData) => {
  newChatListeners.forEach(listener => listener(chat));
};

export const useYourChats = (options: UseYourChatsOptions = {}) => {
  const { autoFetchOnMount = true, pageSize = 10 } = options;

  // State - initialize from persisted values
  const [yourChatsExpanded, setYourChatsExpandedState] = useState(persistedYourChatsExpanded);
  const [expandedCategories, setExpandedCategoriesState] = useState<string[]>(persistedExpandedCategories);

  // Wrapper setters that also persist the state
  const setYourChatsExpanded = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
    setYourChatsExpandedState(prev => {
      const newValue = typeof value === 'function' ? value(prev) : value;
      persistedYourChatsExpanded = newValue;
      return newValue;
    });
  }, []);

  const setExpandedCategories = useCallback((value: string[] | ((prev: string[]) => string[])) => {
    setExpandedCategoriesState(prev => {
      const newValue = typeof value === 'function' ? value(prev) : value;
      persistedExpandedCategories = newValue;
      return newValue;
    });
  }, []);
  const [chatsByCategory, setChatsByCategory] = useState<Record<string, any[]>>({
    General: [],
  });
  const [loadingByCategory, setLoadingByCategory] = useState<Record<string, boolean>>({
    General: false,
  });
  const [loadingMoreByCategory, setLoadingMoreByCategory] = useState<Record<string, boolean>>({
    General: false,
  });
  const [paginationByCategory, setPaginationByCategory] = useState<Record<string, PaginationState>>({
    General: { currentPage: 1, totalPages: 1, totalCount: 0 },
  });

  // Track fetched pages to avoid duplicate fetches
  const fetchedPagesRef = useRef<Record<string, Set<number>>>({
    General: new Set(),
  });

  // Fetch chats for a category with pagination support
  const fetchChatsForCategory = useCallback(async (
    categoryId: string,
    page: number = 1,
    append: boolean = false
  ) => {
    // Check if this page was already fetched
    if (fetchedPagesRef.current[categoryId]?.has(page)) {
      return;
    }

    // Mark page as being fetched
    fetchedPagesRef.current[categoryId]?.add(page);

    if (page === 1) {
      setLoadingByCategory(prev => ({ ...prev, [categoryId]: true }));
    } else {
      setLoadingMoreByCategory(prev => ({ ...prev, [categoryId]: true }));
    }

    try {
      const response = await axios.post(
        getApiEndpoint('GET_DESIGN_CHAT_SESSIONS_LIST'),
        { category: categoryId, page, limit: pageSize },
        { withCredentials: true }
      );
      const data = response.data?.message;

      if (data?.success && Array.isArray(data?.sessions)) {
        setChatsByCategory(prev => ({
          ...prev,
          [categoryId]: append ? [...prev[categoryId], ...data.sessions] : data.sessions
        }));

        // Update pagination state
        if (data?.pagination) {
          setPaginationByCategory(prev => ({
            ...prev,
            [categoryId]: {
              currentPage: data.pagination.page || page,
              totalPages: data.pagination.total_pages || Math.ceil((data.pagination.total_count || data.sessions.length) / (data.pagination.limit || pageSize)),
              totalCount: data.pagination.total_count || data.pagination.total || 0,
            },
          }));
        } else {
          setPaginationByCategory(prev => ({
            ...prev,
            [categoryId]: {
              currentPage: page,
              totalPages: data.sessions.length === pageSize ? page + 1 : page,
              totalCount: data.total_count || prev[categoryId]?.totalCount || 0,
            },
          }));
        }
      } else if (Array.isArray(data)) {
        setChatsByCategory(prev => ({
          ...prev,
          [categoryId]: append ? [...prev[categoryId], ...data] : data
        }));
        setPaginationByCategory(prev => ({
          ...prev,
          [categoryId]: {
            currentPage: page,
            totalPages: data.length === pageSize ? page + 1 : page,
            totalCount: prev[categoryId]?.totalCount || 0,
          },
        }));
      }
    } catch (error) {
      logger.error(`Error fetching ${categoryId} chats:`, error);
      // Remove page from fetched set on error so it can be retried
      fetchedPagesRef.current[categoryId]?.delete(page);
    } finally {
      setLoadingByCategory(prev => ({ ...prev, [categoryId]: false }));
      setLoadingMoreByCategory(prev => ({ ...prev, [categoryId]: false }));
    }
  }, [pageSize]);

  // Fetch all categories (for initial load)
  const fetchAllCategories = useCallback(async () => {
    for (const category of CHAT_CATEGORY_CONFIG) {
      await fetchChatsForCategory(category.id, 1, false);
    }
  }, [fetchChatsForCategory]);

  // Toggle category expansion (only one category open at a time)
  const toggleCategory = useCallback((categoryId: string) => {
    if (expandedCategories.includes(categoryId)) {
      // Close if already open
      setExpandedCategories([]);
    } else {
      // Close others and open this one
      setExpandedCategories([categoryId]);
      // Fetch chats if not already loaded
      if (chatsByCategory[categoryId].length === 0) {
        fetchChatsForCategory(categoryId, 1, false);
      }
    }
  }, [expandedCategories, chatsByCategory, fetchChatsForCategory]);

  // Load more chats for a category
  const loadMoreForCategory = useCallback((categoryId: string) => {
    const pagination = paginationByCategory[categoryId];
    if (pagination && pagination.currentPage < pagination.totalPages && !loadingMoreByCategory[categoryId]) {
      fetchChatsForCategory(categoryId, pagination.currentPage + 1, true);
    }
  }, [paginationByCategory, loadingMoreByCategory, fetchChatsForCategory]);

  // Get total sessions count (uses actual total from API, not loaded count)
  const totalSessionsCount = useMemo(() => {
    return Object.values(paginationByCategory).reduce((sum, pagination) => sum + pagination.totalCount, 0);
  }, [paginationByCategory]);

  // Get count for a specific category (uses actual total from API)
  const getCategoryCount = useCallback((categoryId: string) => {
    return paginationByCategory[categoryId]?.totalCount || 0;
  }, [paginationByCategory]);

  // Reset fetched pages (useful for refresh)
  const resetFetchedPages = useCallback(() => {
    fetchedPagesRef.current = {
      General: new Set(),
    };
  }, []);

  // Refresh all chats
  const refreshChats = useCallback(async () => {
    resetFetchedPages();
    setChatsByCategory({
      General: [],
    });
    await fetchAllCategories();
  }, [resetFetchedPages, fetchAllCategories]);

  // Delete a chat session
  const deleteChat = useCallback(async (sessionId: string, categoryId: string): Promise<boolean> => {
    try {
      const response = await axios.post(
        getApiEndpoint('DELETE_DESIGN_CHAT_SESSION'),
        { session_id: sessionId },
        { withCredentials: true }
      );

      const data = response.data?.message;

      if (data?.success) {
        // Remove the chat from local state
        setChatsByCategory(prev => ({
          ...prev,
          [categoryId]: prev[categoryId].filter((chat: any) => chat.name !== sessionId)
        }));

        // Update the count
        setPaginationByCategory(prev => ({
          ...prev,
          [categoryId]: {
            ...prev[categoryId],
            totalCount: Math.max(0, prev[categoryId].totalCount - 1)
          }
        }));

        toast.success('Chat deleted successfully');
        return true;
      } else {
        toast.error(data?.message || 'Failed to delete chat');
        return false;
      }
    } catch (error: any) {
      logger.error('Error deleting chat:', error);
      const errorMessage = error?.response?.data?.message?.message || error?.message || 'Failed to delete chat';
      toast.error(errorMessage);
      return false;
    }
  }, []);

  // Update chat title in local state (called via triggerChatTitleUpdate)
  const updateChatTitle = useCallback((sessionId: string, newTitle: string) => {
    setChatsByCategory(prev => {
      const updated = { ...prev };
      // Search across all categories for the session
      for (const categoryId of Object.keys(updated)) {
        updated[categoryId] = updated[categoryId].map((chat: any) => {
          if (chat.name === sessionId) {
            return { ...chat, session_title: newTitle, title: newTitle };
          }
          return chat;
        });
      }
      return updated;
    });
  }, []);

  // Register listener for external chat title updates
  useEffect(() => {
    chatUpdateListeners.add(updateChatTitle);
    return () => {
      chatUpdateListeners.delete(updateChatTitle);
    };
  }, [updateChatTitle]);

  // Add new chat to local state (called via triggerNewChatAdded)
  const addNewChat = useCallback((chat: NewChatData) => {
    const newChatEntry = {
      name: chat.sessionId,
      session_title: chat.sessionTitle,
      title: chat.sessionTitle,
      started_at: chat.startedAt,
      category: chat.category,
    };

    setChatsByCategory(prev => ({
      ...prev,
      [chat.category]: [newChatEntry, ...prev[chat.category]],
    }));

    // Update pagination count
    setPaginationByCategory(prev => ({
      ...prev,
      [chat.category]: {
        ...prev[chat.category],
        totalCount: prev[chat.category].totalCount + 1,
      },
    }));
  }, []);

  // Register listener for new chat additions
  useEffect(() => {
    newChatListeners.add(addNewChat);
    return () => {
      newChatListeners.delete(addNewChat);
    };
  }, [addNewChat]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetchOnMount) {
      fetchAllCategories();
    }
  }, [autoFetchOnMount, fetchAllCategories]);

  return {
    // State
    yourChatsExpanded,
    setYourChatsExpanded,
    expandedCategories,
    chatsByCategory,
    loadingByCategory,
    loadingMoreByCategory,
    paginationByCategory,
    totalSessionsCount,

    // Actions
    toggleCategory,
    fetchChatsForCategory,
    fetchAllCategories,
    loadMoreForCategory,
    refreshChats,
    resetFetchedPages,
    getCategoryCount,
    deleteChat,
  };
};
